import { useState, useRef, useEffect, useCallback } from "react";

const useSpeciesSearch = (searchTerm, searchSpecies, options = {}) => {
  const {
    includeFish = true,
    includeInvertebrates = true,
    includePlants = true,
    debounceMs = 500,
    onSearchStart,
    onSearchClear,
  } = options;

  const [searchResults, setSearchResults] = useState({});
  const [loadingSearch, setLoadingSearch] = useState(false);
  const searchTermRef = useRef("");
  const searchResultsForTermRef = useRef("");
  const abortControllerRef = useRef(null);
  const onSearchStartRef = useRef(onSearchStart);
  const onSearchClearRef = useRef(onSearchClear);

  onSearchStartRef.current = onSearchStart;
  onSearchClearRef.current = onSearchClear;

  const fetchSearchResults = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const currentTerm = searchTermRef.current;
      const data = await searchSpecies(
        currentTerm,
        abortController.signal,
        includeFish,
        includeInvertebrates,
        includePlants
      );

      if (
        abortController.signal.aborted ||
        searchTermRef.current !== currentTerm
      ) {
        return;
      }

      if (data == null) {
        setSearchResults({});
        searchResultsForTermRef.current = "";
        setLoadingSearch(false);
        return;
      }

      setSearchResults(data);
      searchResultsForTermRef.current = currentTerm;
      setLoadingSearch(false);
    } catch (error) {
      if (
        error.name !== "AbortError" &&
        abortControllerRef.current === abortController
      ) {
        console.error("Error fetching search results:", error);
        setSearchResults({});
        searchResultsForTermRef.current = "";
        setLoadingSearch(false);
      }
    }
  }, [searchSpecies, includeFish, includeInvertebrates, includePlants]);

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults({});
      searchResultsForTermRef.current = "";
      setLoadingSearch(false);
      onSearchClearRef.current?.();
      return;
    }

    setLoadingSearch(true);
    searchTermRef.current = searchTerm;
    onSearchStartRef.current?.();

    const timeoutId = setTimeout(() => {
      fetchSearchResults();
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    searchResults,
    loadingSearch,
    searchResultsForTermRef,
  };
};

export default useSpeciesSearch;
